/**
 * Panel de ajustes de la IA: lo que el jefe puede cambiar sin llamar a nadie.
 *
 * Peticion de Ricardo (05/08/2026): poder tocar el cerebro que piensa, como se
 * comporta, con que voz habla y guardar instrucciones propias SIN entrar en
 * `settings.ini` ni reiniciar el servicio. El backend ya lo valida y lo aplica
 * en caliente (`backend/api/panel.py`); esto es su cara.
 *
 * Decisiones que vienen del usuario real —88 anos, kiosco a pantalla completa,
 * nadie al lado— y que conviene no deshacer al retocar el diseno:
 *
 *  - **Nada de jerga.** Ningun control se llama como se llama por dentro: no
 *    hay "temperatura" ni "modelo", hay "cuánto se arriesga al responder" y
 *    "qué cerebro usa para pensar". El nombre tecnico no ayuda a nadie aqui.
 *  - **Elegir, no atinar.** El riesgo y las cantidades se eligen en listas y
 *    con botones grandes; ninguna barra que haya que arrastrar con precision.
 *  - **Capa que se cierra.** Entra por un boton claro y sale con Cerrar o con
 *    Escape. La conversacion sigue MONTADA debajo (el socket de voz y el
 *    dictado a medias no se pierden por abrir los ajustes).
 *  - **El error del servidor se ensena tal cual.** `ErrorPreferencia` ya lo
 *    redacta en castellano llano; reescribirlo aqui solo lograria que el panel
 *    y el nucleo dijeran cosas distintas.
 */
import { useCallback, useEffect, useState } from 'react';
import { Boton } from '@/components/ui/button';
import {
  MOTIVO_TEMPERATURA_BLOQUEADA,
  NIVELES_RIESGO,
  alternarInstruccion,
  avisoDePrivacidad,
  borrarInstruccion,
  cambiosPendientes,
  esModeloLocal,
  etiquetaRiesgo,
  guardarInstruccion,
  hayCambios,
  mensajeDeFallo,
  nivelRiesgoMasCercano,
  nombreDeModelo,
  tocaRecargarOpciones,
  validarInstruccion,
} from '@/lib/ajustes';
import { api, type AjustesIA, type OpcionesAjustes } from '@/lib/api';
import { ESCALAS, type Escala } from '@/lib/escala';
import { cn } from '@/lib/utils';

interface Props {
  /** Cierra el panel y devuelve al jefe a la conversacion. */
  alCerrar: () => void;
  /** Tamano de interfaz elegido en ESTE dispositivo (no viaja al servidor). */
  escala: Escala;
  alCambiarEscala: (escala: Escala) => void;
  className?: string;
}

/** Clase comun de los desplegables: alto tactil y letra grande. */
const CLASE_CAMPO =
  'min-h-tactil w-full rounded-lg border-2 border-input bg-background px-4 text-lg ' +
  'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring ' +
  'disabled:cursor-not-allowed disabled:opacity-60';

/** Valor centinela del desplegable para "quiero escribirlo yo". */
const A_MANO = '__a_mano__';

interface PropsSelector {
  idCampo: string;
  etiqueta: string;
  ayuda?: string;
  modelos: string[];
  valor: string;
  alCambiar: (valor: string) => void;
}

/**
 * Un cerebro: lista de los conocidos + la opcion de escribir el nombre.
 *
 * El campo libre existe porque la lista se queda vieja (Ricardo, 09/09/2026):
 * un modelo recien bajado con `ollama pull`, o un Claude nuevo, no tienen por
 * que esperar a que alguien los anada al codigo. El backend lo comprueba en lo
 * que puede —la forma siempre, y si es local le pregunta a Ollama si de verdad
 * lo tiene— y si no vale contesta el motivo en castellano.
 */
function SelectorModelo({ idCampo, etiqueta, ayuda, modelos, valor, alCambiar }: PropsSelector) {
  const enLista = modelos.includes(valor);
  const [aMano, setAMano] = useState(!enLista);
  const local = esModeloLocal(valor);

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={idCampo} className="text-lg font-semibold">
        {etiqueta}
      </label>
      {ayuda && <p className="text-base text-muted-foreground">{ayuda}</p>}
      <select
        id={idCampo}
        className={CLASE_CAMPO}
        value={aMano ? A_MANO : valor}
        onChange={(e) => {
          if (e.target.value === A_MANO) {
            setAMano(true);
            return;
          }
          setAMano(false);
          alCambiar(e.target.value);
        }}
      >
        {modelos.map((m) => (
          <option key={m} value={m}>
            {nombreDeModelo(m)}
          </option>
        ))}
        {/* Si lo que hay puesto no está en la lista (se escribió a mano), se
            enseña igual: si no, el desplegable mentiría sobre lo que corre. */}
        {!enLista && !aMano && <option value={valor}>{valor}</option>}
        <option value={A_MANO}>Escribir otro nombre…</option>
      </select>

      {aMano && (
        <input
          type="text"
          aria-label={`Nombre del modelo para: ${etiqueta}`}
          className={CLASE_CAMPO}
          placeholder="ollama:qwen3:8b"
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          value={valor}
          onChange={(e) => alCambiar(e.target.value.trim())}
        />
      )}

      <p
        className={cn(
          'rounded-lg border-2 p-3 text-base',
          local
            ? 'border-[hsl(var(--cobro))] bg-[hsl(var(--cobro))]/10'
            : 'border-border bg-muted/50 text-muted-foreground',
        )}
      >
        {avisoDePrivacidad(valor)}
      </p>
    </div>
  );
}

export function PanelAjustes({ alCerrar, escala, alCambiarEscala, className }: Props) {
  /** Lo ultimo confirmado por el servidor: la referencia de "que ha cambiado". */
  const [guardados, setGuardados] = useState<AjustesIA | null>(null);
  /** Lo que hay ahora en pantalla, pendiente de guardar. */
  const [borrador, setBorrador] = useState<AjustesIA | null>(null);
  const [opciones, setOpciones] = useState<OpcionesAjustes | null>(null);

  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  /** Acuse visible del ultimo guardado ("Guardado"). */
  const [acuse, setAcuse] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Formulario de instruccion nueva.
  const [nombreNuevo, setNombreNuevo] = useState('');
  const [textoNuevo, setTextoNuevo] = useState('');

  /** Trae los ajustes vigentes y entre que valores se puede elegir. */
  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await api.ajustes();
      setGuardados(r.ajustes);
      setBorrador(r.ajustes);
      setOpciones(r.opciones);
      setError(null);
    } catch (fallo) {
      setError(mensajeDeFallo(fallo));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  // Escape cierra: el kiosco tiene teclado y es el gesto que ya conoce de
  // cualquier ventana. El boton Cerrar sigue siendo el camino principal.
  useEffect(() => {
    const alPulsar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') alCerrar();
    };
    window.addEventListener('keydown', alPulsar);
    return () => window.removeEventListener('keydown', alPulsar);
  }, [alCerrar]);

  /** Cambia una sola clave del borrador y retira el acuse anterior. */
  const tocar = useCallback(<C extends keyof AjustesIA>(clave: C, valor: AjustesIA[C]) => {
    setAcuse(null);
    setError(null);
    setBorrador((previo) => (previo ? { ...previo, [clave]: valor } : previo));
  }, []);

  const guardar = useCallback(async () => {
    if (!guardados || !borrador) return;
    const cambios = cambiosPendientes(guardados, borrador);
    if (Object.keys(cambios).length === 0) return;

    setGuardando(true);
    setError(null);
    try {
      const r = await api.guardarAjustes(cambios);
      setGuardados(r.ajustes);
      setBorrador(r.ajustes);
      setAcuse('Guardado. Lo notará en la próxima respuesta.');
      // Cambiar de cerebro cambia si se puede o no tocar el riesgo, y eso solo
      // lo sabe el backend: se vuelven a pedir las opciones.
      if (tocaRecargarOpciones(cambios)) {
        const fresco = await api.ajustes();
        setOpciones(fresco.opciones);
      }
    } catch (fallo) {
      setAcuse(null);
      setError(mensajeDeFallo(fallo));
    } finally {
      setGuardando(false);
    }
  }, [borrador, guardados]);

  const restablecer = useCallback(async () => {
    setGuardando(true);
    setError(null);
    try {
      const r = await api.restablecerAjustes();
      setGuardados(r.ajustes);
      setBorrador(r.ajustes);
      setAcuse('Todo ha vuelto a como estaba de fábrica.');
      const fresco = await api.ajustes();
      setOpciones(fresco.opciones);
    } catch (fallo) {
      setAcuse(null);
      setError(mensajeDeFallo(fallo));
    } finally {
      setGuardando(false);
    }
  }, []);

  /** Anade la instruccion escrita abajo a la biblioteca (sin guardar aun). */
  const anadirInstruccion = useCallback(() => {
    const motivo = validarInstruccion(nombreNuevo, textoNuevo);
    if (motivo) {
      setAcuse(null);
      setError(motivo);
      return;
    }
    setError(null);
    setAcuse(null);
    setBorrador((previo) =>
      previo
        ? { ...previo, instrucciones: guardarInstruccion(previo.instrucciones, nombreNuevo, textoNuevo) }
        : previo,
    );
    setNombreNuevo('');
    setTextoNuevo('');
  }, [nombreNuevo, textoNuevo]);

  const pendiente = guardados && borrador ? hayCambios(guardados, borrador) : false;

  return (
    <div
      className={cn(
        'fixed inset-0 z-40 overflow-y-auto bg-background/98 backdrop-blur-sm',
        className,
      )}
      role="dialog"
      aria-modal="true"
      aria-labelledby="titulo-ajustes"
    >
      <div className="mx-auto flex max-w-4xl flex-col gap-6 p-5 lg:p-8">
        <header className="flex items-center justify-between gap-4">
          <h2 id="titulo-ajustes" className="text-3xl font-bold">
            Ajustes de Androcles
          </h2>
          <Boton variante="contorno" onClick={alCerrar}>
            Cerrar
          </Boton>
        </header>

        {/* Avisos de estado: uno solo, siempre en el mismo sitio, y anunciado
            en voz para quien use lector de pantalla. */}
        <div aria-live="polite" className="flex flex-col gap-3">
          {cargando && <p className="text-lg text-muted-foreground">Cargando los ajustes…</p>}
          {acuse && (
            <p className="rounded-lg border-2 border-[hsl(var(--cobro))] bg-[hsl(var(--cobro))]/10 p-4 text-lg font-semibold">
              {acuse}
            </p>
          )}
        </div>
        {error && (
          <p
            role="alert"
            className="rounded-lg border-2 border-destructive bg-destructive/10 p-4 text-lg"
          >
            {error}
          </p>
        )}

        {/* --- Tamaño de la pantalla ----------------------------------------
            Va PRIMERO y FUERA del bloque que depende del servidor: es un
            ajuste de este navegador, y si el backend no contesta el jefe tiene
            que poder encoger la pantalla igual. Se aplica al pulsar, sin
            Guardar: se ve el efecto en el acto y no hay nada que confirmar. */}
        <section className="tarjeta-kiosco flex flex-col gap-4" aria-labelledby="ajustes-tamano">
          <h3 id="ajustes-tamano" className="text-2xl font-bold">
            Tamaño de la pantalla
          </h3>
          <p className="text-base text-muted-foreground">
            Solo afecta a este aparato. El kiosco del despacho se queda como está.
          </p>
          <div className="flex flex-col gap-3">
            {ESCALAS.map((opcion) => {
              const activa = escala === opcion.clave;
              return (
                <button
                  key={opcion.clave}
                  type="button"
                  aria-pressed={activa}
                  onClick={() => alCambiarEscala(opcion.clave)}
                  className={cn(
                    'min-h-tactil rounded-lg border-2 px-4 py-3 text-left transition-colors',
                    activa
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:bg-muted',
                  )}
                >
                  <span className="block text-lg font-semibold">{opcion.etiqueta}</span>
                  <span className="block text-base text-muted-foreground">{opcion.ayuda}</span>
                </button>
              );
            })}
          </div>
        </section>

        {borrador && opciones && (
          <>
            {/* --- Cómo piensa ------------------------------------------- */}
            <section className="tarjeta-kiosco flex flex-col gap-6" aria-labelledby="ajustes-pensar">
              <h3 id="ajustes-pensar" className="text-2xl font-bold">
                Cómo piensa
              </h3>

              {opciones.roles.map((rol, indice) => (
                <SelectorModelo
                  key={rol.clave}
                  idCampo={`ajuste-modelo-${rol.clave}`}
                  etiqueta={rol.etiqueta}
                  ayuda={
                    indice === 0
                      ? 'El que le contesta cuando habla con él. Los más potentes contestan mejor, pero tardan un poco más.'
                      : undefined
                  }
                  modelos={opciones.modelos}
                  valor={(borrador[`modelo_${rol.clave}` as keyof AjustesIA] as string) ?? ''}
                  alCambiar={(v) => tocar(`modelo_${rol.clave}` as keyof AjustesIA, v as never)}
                />
              ))}

              <div className="flex flex-col gap-2">
                <label htmlFor="ajuste-riesgo" className="text-lg font-semibold">
                  Cuánto se arriesga al responder
                </label>
                <p className="text-base text-muted-foreground">
                  Para hablar de dinero conviene tenerlo prudente.
                </p>
                <select
                  id="ajuste-riesgo"
                  className={CLASE_CAMPO}
                  value={String(nivelRiesgoMasCercano(borrador.temperatura))}
                  disabled={!opciones.temperatura_aplicable}
                  aria-describedby={
                    opciones.temperatura_aplicable ? undefined : 'motivo-riesgo-bloqueado'
                  }
                  onChange={(e) => tocar('temperatura', Number(e.target.value))}
                >
                  {NIVELES_RIESGO.map((nivel) => (
                    <option key={nivel} value={String(nivel)}>
                      {etiquetaRiesgo(nivel)}
                    </option>
                  ))}
                </select>
                {!opciones.temperatura_aplicable && (
                  <p
                    id="motivo-riesgo-bloqueado"
                    className="rounded-lg border-2 border-border bg-muted/60 p-4 text-base"
                  >
                    {MOTIVO_TEMPERATURA_BLOQUEADA}
                  </p>
                )}
              </div>

              <CampoCantidad
                id="ajuste-vueltas"
                etiqueta="Cuántas vueltas puede dar buscando el dato"
                ayuda="Si se queda corto, deja la respuesta a medias; si se pasa, tarda más."
                valor={borrador.max_iteraciones}
                minimo={1}
                maximo={30}
                alCambiar={(v) => tocar('max_iteraciones', v)}
              />
            </section>

            {/* --- Cerebro local -----------------------------------------
                Solo se pinta si hay alguno puesto: al que no use modelos
                locales, esto no le dice nada y solo le llena la pantalla. */}
            {[
              borrador.modelo_orquestador,
              borrador.modelo_asesor,
              borrador.modelo_contable,
              borrador.modelo_nlu_voz,
              borrador.modelo_consejero_senior,
            ].some(esModeloLocal) && (
              <section
                className="tarjeta-kiosco flex flex-col gap-6"
                aria-labelledby="ajustes-local"
              >
                <h3 id="ajustes-local" className="text-2xl font-bold">
                  El cerebro de casa
                </h3>
                <p className="text-base text-muted-foreground">
                  Ajustes del programa que hace pensar a los modelos locales (Ollama).
                </p>

                <div className="flex flex-col gap-2">
                  <label htmlFor="ajuste-ollama-url" className="text-lg font-semibold">
                    En qué ordenador está
                  </label>
                  <p className="text-base text-muted-foreground">
                    Solo se admite este ordenador o uno de la oficina. Si pone una
                    dirección de internet, la tesorería saldría fuera y no se guarda.
                  </p>
                  <input
                    id="ajuste-ollama-url"
                    type="text"
                    className={CLASE_CAMPO}
                    spellCheck={false}
                    autoCapitalize="off"
                    autoCorrect="off"
                    value={borrador.ollama_url}
                    onChange={(e) => tocar('ollama_url', e.target.value.trim())}
                  />
                </div>

                <CampoCantidad
                  id="ajuste-ollama-contexto"
                  etiqueta="Cuánta conversación recuerda de golpe"
                  ayuda="Más es mejor memoria dentro del rato, pero ocupa más tarjeta gráfica."
                  valor={borrador.ollama_contexto}
                  minimo={4096}
                  maximo={131072}
                  paso={4096}
                  alCambiar={(v) => tocar('ollama_contexto', v)}
                />

                <CampoCantidad
                  id="ajuste-ollama-espera"
                  etiqueta="Cuánto se le espera como máximo (segundos)"
                  ayuda="La primera pregunta del día tarda más: tiene que cargarse en la gráfica."
                  valor={borrador.ollama_timeout_s}
                  minimo={10}
                  maximo={600}
                  paso={10}
                  alCambiar={(v) => tocar('ollama_timeout_s', v)}
                />

                <Interruptor
                  id="ajuste-ollama-pensar"
                  etiqueta="Dejarle pensar en voz alta antes de contestar"
                  ayuda="Normalmente NO: si lo hace, el kiosco le leería en voz alta todo su razonamiento."
                  valor={borrador.ollama_pensar}
                  alCambiar={(v) => tocar('ollama_pensar', v)}
                />
              </section>
            )}

            {/* --- Cómo se comporta -------------------------------------- */}
            <section
              className="tarjeta-kiosco flex flex-col gap-6"
              aria-labelledby="ajustes-comportamiento"
            >
              <h3 id="ajustes-comportamiento" className="text-2xl font-bold">
                Cómo se comporta
              </h3>

              <CampoCantidad
                id="ajuste-frases"
                etiqueta="Cuántas frases dice de una vez"
                ayuda="Cuantas menos, más corto y más fácil de seguir de oído."
                valor={borrador.frases_respuesta}
                minimo={1}
                maximo={10}
                alCambiar={(v) => tocar('frases_respuesta', v)}
              />

              <Interruptor
                id="ajuste-contexto"
                etiqueta="Mirar la caja antes de que usted pregunte"
                ayuda="Contesta antes a lo del día a día, porque ya lo trae mirado."
                valor={borrador.contexto_adelantado}
                alCambiar={(v) => tocar('contexto_adelantado', v)}
              />

              <Interruptor
                id="ajuste-memoria"
                etiqueta="Recordar lo que usted le pida recordar"
                ayuda="Solo guarda lo que usted le diga expresamente que guarde."
                valor={borrador.memoria_activa}
                alCambiar={(v) => tocar('memoria_activa', v)}
              />
            </section>

            {/* --- Voz --------------------------------------------------- */}
            <section className="tarjeta-kiosco flex flex-col gap-6" aria-labelledby="ajustes-voz">
              <h3 id="ajustes-voz" className="text-2xl font-bold">
                Cómo habla
              </h3>

              <div className="flex flex-col gap-2">
                <label htmlFor="ajuste-voz" className="text-lg font-semibold">
                  Con qué voz le habla
                </label>
                <select
                  id="ajuste-voz"
                  className={CLASE_CAMPO}
                  value={`${borrador.motor_voz}|${borrador.voz}`}
                  onChange={(e) => {
                    const [motor, voz] = e.target.value.split('|');
                    setAcuse(null);
                    setError(null);
                    setBorrador((previo) =>
                      previo ? { ...previo, motor_voz: motor, voz } : previo,
                    );
                  }}
                >
                  {/* Si la voz guardada no esta en el catalogo (settings.ini con
                      una voz retirada), se anade para que el desplegable no
                      aparezca en blanco ni cambie la voz a sus espaldas. */}
                  {!opciones.voces.some(
                    (o) => o.motor === borrador.motor_voz && o.voz === borrador.voz,
                  ) && (
                    <option value={`${borrador.motor_voz}|${borrador.voz}`}>
                      {borrador.voz || 'La que venga por defecto'}
                    </option>
                  )}
                  {opciones.voces.map((o) => (
                    <option
                      key={`${o.motor}|${o.voz}`}
                      value={`${o.motor}|${o.voz}`}
                      disabled={!o.disponible}
                    >
                      {o.etiqueta}
                      {o.en_la_nube ? ' — por internet' : ''}
                      {o.disponible ? '' : ' (no se puede usar)'}
                    </option>
                  ))}
                </select>

                {/* Por qué está en gris. Antes solo se pintaba apagada y el
                    jefe no sabía si faltaba descargar algo, si había que
                    contratar una clave o si era una avería. */}
                {(() => {
                  const elegida = opciones.voces.find(
                    (o) => o.motor === borrador.motor_voz && o.voz === borrador.voz,
                  );
                  if (!elegida) return null;
                  if (!elegida.disponible) {
                    return (
                      <p className="rounded-lg border-2 border-border bg-muted/60 p-4 text-base">
                        {elegida.motivo}
                      </p>
                    );
                  }
                  if (elegida.en_la_nube) {
                    return (
                      <p className="rounded-lg border-2 border-[hsl(var(--alerta))] bg-[hsl(var(--alerta))]/10 p-4 text-base">
                        Ojo: esta voz se genera por internet. Lo que Androcles le
                        lee en voz alta —cifras incluidas— sale de esta casa. Las
                        demás voces suenan aquí dentro.
                      </p>
                    );
                  }
                  return null;
                })()}
              </div>

              <Interruptor
                id="ajuste-streaming"
                etiqueta="Empezar a hablar mientras piensa"
                ayuda="Empieza a contestar antes, frase a frase, en vez de esperar al final."
                valor={borrador.voz_en_streaming}
                alCambiar={(v) => tocar('voz_en_streaming', v)}
              />
            </section>

            {/* --- Instrucciones del jefe -------------------------------- */}
            <section
              className="tarjeta-kiosco flex flex-col gap-6"
              aria-labelledby="ajustes-instrucciones"
            >
              <h3 id="ajustes-instrucciones" className="text-2xl font-bold">
                Instrucciones suyas
              </h3>
              <p className="text-base text-muted-foreground">
                Cosas que quiere que tenga siempre presentes al hablar con usted. Puede guardar
                varias y encender solo las que le interesen en cada momento.
              </p>

              {borrador.instrucciones.length === 0 && (
                <p className="text-base text-muted-foreground">
                  Todavía no ha guardado ninguna.
                </p>
              )}

              <ul className="flex flex-col gap-4">
                {borrador.instrucciones.map((instruccion) => (
                  <li
                    key={instruccion.nombre}
                    className="flex flex-col gap-3 rounded-lg border-2 border-border p-4"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex flex-col gap-1">
                        <strong className="text-lg">{instruccion.nombre}</strong>
                        <span className="text-base text-muted-foreground">
                          {instruccion.activa ? 'Encendida' : 'Apagada'}
                        </span>
                      </div>
                      <div className="flex shrink-0 gap-3">
                        <Boton
                          variante={instruccion.activa ? 'afirmativo' : 'contorno'}
                          onClick={() => {
                            setAcuse(null);
                            setBorrador((previo) =>
                              previo
                                ? {
                                    ...previo,
                                    instrucciones: alternarInstruccion(
                                      previo.instrucciones,
                                      instruccion.nombre,
                                    ),
                                  }
                                : previo,
                            );
                          }}
                          aria-label={`${instruccion.activa ? 'Apagar' : 'Encender'} la instrucción ${instruccion.nombre}`}
                        >
                          {instruccion.activa ? 'Apagar' : 'Encender'}
                        </Boton>
                        <Boton
                          variante="fantasma"
                          onClick={() => {
                            setAcuse(null);
                            setBorrador((previo) =>
                              previo
                                ? {
                                    ...previo,
                                    instrucciones: borrarInstruccion(
                                      previo.instrucciones,
                                      instruccion.nombre,
                                    ),
                                  }
                                : previo,
                            );
                          }}
                          aria-label={`Borrar la instrucción ${instruccion.nombre}`}
                        >
                          Borrar
                        </Boton>
                      </div>
                    </div>
                    <p className="whitespace-pre-wrap text-base">{instruccion.texto}</p>
                  </li>
                ))}
              </ul>

              <div className="flex flex-col gap-3 rounded-lg border-2 border-dashed border-border p-4">
                <label htmlFor="instruccion-nombre" className="text-lg font-semibold">
                  Nombre (para reconocerla luego)
                </label>
                <input
                  id="instruccion-nombre"
                  className={CLASE_CAMPO}
                  value={nombreNuevo}
                  maxLength={80}
                  onChange={(e) => setNombreNuevo(e.target.value)}
                />

                <label htmlFor="instruccion-texto" className="text-lg font-semibold">
                  Qué quiere que haga
                </label>
                <textarea
                  id="instruccion-texto"
                  className={cn(CLASE_CAMPO, 'min-h-[8rem] resize-y py-3 leading-relaxed')}
                  value={textoNuevo}
                  maxLength={4000}
                  onChange={(e) => setTextoNuevo(e.target.value)}
                />

                <Boton variante="secundario" onClick={anadirInstruccion}>
                  Añadir a la lista
                </Boton>
              </div>
            </section>

            {/* --- Guardar ----------------------------------------------- */}
            <div className="sticky bottom-0 flex flex-col gap-3 border-t-2 border-border bg-background p-4 sm:flex-row">
              <Boton
                className="flex-1"
                disabled={!pendiente || guardando}
                onClick={() => void guardar()}
              >
                {guardando ? 'Guardando…' : 'Guardar'}
              </Boton>
              <Boton
                variante="contorno"
                className="flex-1"
                disabled={guardando}
                onClick={() => void restablecer()}
              >
                Dejarlo como estaba de fábrica
              </Boton>
              <Boton variante="fantasma" className="flex-1" onClick={alCerrar}>
                Cerrar
              </Boton>
            </div>
            {pendiente && !guardando && (
              <p className="pb-6 text-base text-muted-foreground" aria-live="polite">
                Hay cambios sin guardar.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Cantidad pequena con botones de menos y mas.
 *
 * Un `input type=number` obliga a acertar en una flechita de 12 px o a borrar y
 * teclear; aqui basta con pulsar dos botones grandes, y el numero se ve en
 * letra grande al lado. El campo sigue siendo un `input` de verdad (con su
 * `label`) para que el teclado y los lectores de pantalla lo entiendan.
 */
function CampoCantidad({
  id,
  etiqueta,
  ayuda,
  valor,
  minimo,
  maximo,
  paso = 1,
  alCambiar,
}: {
  id: string;
  etiqueta: string;
  ayuda: string;
  valor: number;
  minimo: number;
  maximo: number;
  /** Cuanto sube o baja cada pulsacion. En cantidades grandes (el contexto del
   *  cerebro local va de 4.096 a 131.072) ir de uno en uno no llegaria nunca. */
  paso?: number;
  alCambiar: (valor: number) => void;
}) {
  const acotar = (v: number) => Math.min(maximo, Math.max(minimo, v));

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="text-lg font-semibold">
        {etiqueta}
      </label>
      <p id={`${id}-ayuda`} className="text-base text-muted-foreground">
        {ayuda}
      </p>
      <div className="flex items-center gap-3">
        <Boton
          variante="contorno"
          tamano="icono"
          onClick={() => alCambiar(acotar(valor - paso))}
          disabled={valor <= minimo}
          aria-label={`Bajar: ${etiqueta}`}
        >
          −
        </Boton>
        <input
          id={id}
          type="number"
          inputMode="numeric"
          className={cn(CLASE_CAMPO, 'w-36 text-center tabular-nums')}
          value={valor}
          min={minimo}
          max={maximo}
          step={paso}
          aria-describedby={`${id}-ayuda`}
          onChange={(e) => {
            const v = Number(e.target.value);
            if (Number.isFinite(v)) alCambiar(acotar(Math.round(v)));
          }}
        />
        <Boton
          variante="contorno"
          tamano="icono"
          onClick={() => alCambiar(acotar(valor + paso))}
          disabled={valor >= maximo}
          aria-label={`Subir: ${etiqueta}`}
        >
          +
        </Boton>
      </div>
    </div>
  );
}

/**
 * Si o no, en dos botones grandes.
 *
 * Nada de casillas de 16 px: el estado se lee en palabras ("Sí" / "No"
 * resaltado) y el objetivo de pulsacion es el del resto del kiosco.
 */
function Interruptor({
  id,
  etiqueta,
  ayuda,
  valor,
  alCambiar,
}: {
  id: string;
  etiqueta: string;
  ayuda: string;
  valor: boolean;
  alCambiar: (valor: boolean) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <span id={`${id}-etiqueta`} className="text-lg font-semibold">
        {etiqueta}
      </span>
      <p className="text-base text-muted-foreground">{ayuda}</p>
      <div className="flex gap-3" role="group" aria-labelledby={`${id}-etiqueta`}>
        <Boton
          variante={valor ? 'afirmativo' : 'contorno'}
          className="flex-1"
          aria-pressed={valor}
          onClick={() => alCambiar(true)}
        >
          Sí
        </Boton>
        <Boton
          variante={!valor ? 'secundario' : 'contorno'}
          className="flex-1"
          aria-pressed={!valor}
          onClick={() => alCambiar(false)}
        >
          No
        </Boton>
      </div>
    </div>
  );
}
